import { FC, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  NOTIFICATION_KINDS,
  NotificationKind,
  NotificationPreference,
  NotificationSection,
} from "src/shared/api-types/user/notification-preferences.dto";

import { getNotificationPreferences, updateNotificationPreferences } from "src/api/notificationPreferences";
import Button from "src/components/Button";
import useFeatureFlag, { FEATURE_FLAGS } from "src/hooks/useFeatureFlag";
import { captureHandledException } from "src/services/sentry";

type Channel = "email" | "inApp";

const SECTIONS: NotificationSection[] = ["mine", "crew"];

export interface NotificationSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Per notification kind, whether the user gets it in the Notification Centre, by email, both or not
 * at all. The API returns only the kinds that can reach the user, so "My crew" shows up for NPCT and
 * Deputy NPCT only. The In-app column is hidden while the Notification Centre itself is off.
 */
const NotificationSettingsDialog: FC<NotificationSettingsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const showInApp = useFeatureFlag(FEATURE_FLAGS.NOTIFICATION_CENTRE);
  // Two switch columns leave no room for the labels in a phone-width dialog.
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [preferences, setPreferences] = useState<NotificationPreference[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load fresh on every open, so a cancelled edit does not leak into the next one.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setPreferences(null);
    setLoadError(false);
    setSaveError(false);
    getNotificationPreferences()
      .then((response) => {
        if (active) setPreferences(response.preferences);
      })
      .catch((error) => {
        captureHandledException(error, {
          feature: "notification-settings",
          component: "NotificationSettingsDialog",
          action: "load",
        });
        if (active) setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const toggle = (kind: NotificationKind, channel: Channel) =>
    setPreferences(
      (current) => current?.map((p) => (p.kind === kind ? { ...p, [channel]: !p[channel] } : p)) ?? current,
    );

  const handleSave = async () => {
    if (!preferences?.length) return;
    setSaving(true);
    setSaveError(false);
    try {
      await updateNotificationPreferences({ preferences });
      enqueueSnackbar(t("notification_settings.saved"), { variant: "success" });
      onClose();
    } catch (error) {
      captureHandledException(error, {
        feature: "notification-settings",
        component: "NotificationSettingsDialog",
        action: "save",
      });
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const rowsOf = (section: NotificationSection) =>
    NOTIFICATION_KINDS.filter((info) => info.section === section)
      .map((info) => preferences?.find((p) => p.kind === info.kind))
      .filter((p): p is NotificationPreference => Boolean(p));

  const channelSwitch = (preference: NotificationPreference, channel: Channel) => (
    <Switch
      checked={preference[channel]}
      onChange={() => toggle(preference.kind, channel)}
      disabled={saving}
      slotProps={{
        input: {
          "aria-label": `${t(`notification_settings.kinds.${preference.kind}.label`)}: ${t(
            `notification_settings.${channel === "email" ? "email" : "in_app"}`,
          )}`,
        },
      }}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{t("notification_settings.title")}</DialogTitle>
      <DialogContent>
        {loadError && <Alert severity="error">{t("notification_settings.load_error")}</Alert>}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("notification_settings.save_error")}
          </Alert>
        )}
        {!loadError && !preferences && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} aria-label={t("notification_settings.loading")} />
          </Box>
        )}
        {preferences && !preferences.length && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("notification_settings.empty")}
          </Typography>
        )}
        {preferences &&
          SECTIONS.map((section) => {
            const rows = rowsOf(section);
            if (!rows.length) return null;
            return (
              <Box
                key={section}
                component="section"
                aria-labelledby={`notification-settings-${section}`}
                sx={{ mb: 3 }}
              >
                <Typography
                  id={`notification-settings-${section}`}
                  variant="subtitle1"
                  component="h3"
                  sx={{ fontWeight: "bold" }}
                >
                  {t(`notification_settings.sections.${section}`)}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {showInApp && <TableCell align="center">{t("notification_settings.in_app")}</TableCell>}
                      <TableCell align="center">{t("notification_settings.email")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((preference) => (
                      <TableRow key={preference.kind}>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>
                          <Typography variant="body2">
                            {t(`notification_settings.kinds.${preference.kind}.label`)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {t(`notification_settings.kinds.${preference.kind}.hint`)}
                          </Typography>
                        </TableCell>
                        {showInApp && <TableCell align="center">{channelSwitch(preference, "inApp")}</TableCell>}
                        <TableCell align="center">{channelSwitch(preference, "email")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button color="primary" disabled={saving || !preferences?.length} onClick={handleSave}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
